import { useState, useEffect } from 'react'
import { Loading } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack } from '@tabler/icons-react'
import { useInView } from 'react-intersection-observer'
import { AlbumFile } from '@/models/albumFile.ts'
import { useFilesContext } from '@/contexts/filesContext.tsx'
import FilesListing from '@/components/Files/FilesListing.tsx'
import FilesSlideshow from '@/components/Files/FilesSlideshow.tsx'
import useFileSelection from '@/hooks/useFileSelection.tsx'
import ImageSelectionStatus from '@/components/ImageSelectionStatus.tsx'
import { useTranslation } from 'react-i18next'

interface UnifiedImageSelectionViewProps {
  selectedImages: AlbumFile[]
  setSelectedImages: (images: AlbumFile[] | ((prev: AlbumFile[]) => AlbumFile[])) => void
  minImageCount: number | null
  maxImageCount: number | null
  initialSelectedImages?: AlbumFile[]
  onContinue: () => void
  onBack: () => void
  isProcessing?: boolean
  hideOverlay?: boolean
  // Return true if the event was handled and this component shall not do anything
  onFileClick?: (file: AlbumFile) => boolean
  hideSelectionButtons?: boolean
}

export default function UnifiedImageSelectionView({
  selectedImages,
  setSelectedImages,
  minImageCount,
  maxImageCount,
  initialSelectedImages = [],
  onContinue,
  onBack,
  isProcessing = false,
  hideOverlay,
  onFileClick: onFileClickFromProps,
  hideSelectionButtons,
}: UnifiedImageSelectionViewProps) {
  const { t } = useTranslation()
  const { currentFilesCollection, hasNextPage, fetchNextPage, currentAlbum } = useFilesContext()!
  const { ref, inView } = useInView()
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null)
  const fileSelection = useFileSelection({ initialIsSelecting: true })
  const [globalSelectedFileIds, setGlobalSelectedFileIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (initialSelectedImages.length > 0) {
      setSelectedImages(initialSelectedImages)
    }
  }, [initialSelectedImages, setSelectedImages])

  useEffect(() => {
    setGlobalSelectedFileIds(new Set(selectedImages.map(img => img.id)))
  }, [selectedImages])

  const isFileSelected = (file: AlbumFile): boolean => {
    return globalSelectedFileIds.has(file.id)
  }

  const toggleFileSelection = (file: AlbumFile) => {
    if (onFileClickFromProps?.(file)) {
      return
    }

    const isCurrentlySelected = globalSelectedFileIds.has(file.id)

    if (!isCurrentlySelected) {
      // Adding a file - check max limit
      if (maxImageCount !== null && globalSelectedFileIds.size >= maxImageCount) {
        return // Don't add if limit reached
      }
      setSelectedImages(prev => [...prev, file])
    } else {
      setSelectedImages(prev => prev.filter(img => img.id !== file.id))
    }
  }

  function onFileClick(file: AlbumFile) {
    toggleFileSelection(file)
  }

  useEffect(() => {
    if (hasNextPage && fetchNextPage && inView) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  if (!currentFilesCollection) {
    return <Loading color="primary" />
  }

  return (
    <div className="flex flex-col gap-4">
      { !hideSelectionButtons && <div className="flex justify-end items-center gap-3">
        <Button
          color="primary"
          size="sm"
          onClick={() => {
            const newImages = currentFilesCollection.files.filter(file =>
              !selectedImages.some(selected => selected.id === file.id)
            )
            const combined = [...selectedImages, ...newImages]
            // Apply max limit if set
            const final = maxImageCount !== null ? combined.slice(0, maxImageCount) : combined
            setSelectedImages(final)
          }}
          disabled={currentFilesCollection.files.length === 0}
        >
          {t('common.selectAllImagesFromAlbum')}
        </Button>
        {selectedImages.length > 0 && (
          <Button
            color="primary"
            variant="outline"
            size="sm"
            onClick={() => setSelectedImages([])}
          >
            {t('common.removeAll')}
          </Button>
        )}
      </div> }

      <FilesListing
        files={currentFilesCollection}
        onFileClick={onFileClick}
        fileSelection={{
          ...fileSelection,
          isFileSelected,
          toggleFileSelection,
        }}
      />

      <FilesSlideshow
        files={currentFilesCollection.files}
        selectedFileIndex={selectedFileIndex}
        onSelectFileIndex={setSelectedFileIndex}
      />

      <div className="w-1 h-1" ref={ref} />

      {/* Custom overlay */}
      { !hideOverlay &&
      <div className='fixed bottom-0 left-0 w-full z-50 bg-secondary border-t-4 border-primary shadow-lg'>
        <div className='container px-4 mx-auto py-3 flex flex-col gap-2'>
          <div className='flex items-center justify-between flex-row'>
            <div className='flex-1 flex justify-start'>
              <Button
                color="primary"
                variant="outline"
                size="sm"
                onClick={onBack}
              >
                <IconArrowBack size={16} />
                {t('common.back')}
              </Button>
            </div>
            <ImageSelectionStatus
              selectedCount={selectedImages.length}
              minImageCount={minImageCount}
              maxImageCount={maxImageCount}
              isAllSelected={currentAlbum ? fileSelection.isAllSelected(currentAlbum.id) : false}
            />
            <div className='flex-1 flex justify-end'>
              <Button
                color="primary"
                size="sm"
                disabled={
                  selectedImages.length === 0 ||
                  isProcessing ||
                  (minImageCount !== null && selectedImages.length < minImageCount) ||
                  (maxImageCount !== null && selectedImages.length > maxImageCount)
                }
                onClick={onContinue}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      }
    </div>
  )
}
